import { useContext } from "react";
import { AuthContext } from "common/AuthProvider";

// Permission names for the Points area.
//
// These are the exact strings stored in dbo.Permission. AuthProvider compares them with a
// case-sensitive ===, so the casing here has to match the seed migration
// (20260814_AALLAN_01_AddPointHubPermission.sql) character for character.
export const PointPermission = {
  /**
   * Assign a hub to a client point.
   *
   * A point is only reachable from a hub once geo.ClientPointToHub links the two -- the
   * closer's location dropdown is client+hub scoped through that table. Granting this lets
   * operations create the link themselves instead of raising a ticket.
   *
   * Seeded 2026-08-14 to LocationAdmin and Admin.
   *
   * NO LONGER ADD-ONLY, AND THE NAME NOW LIES. It also gates activating and deactivating an
   * existing link from the point detail screen. That was a deliberate call: deactivating a hub
   * link is the same class of act as creating one -- it changes which hubs can be dispatched
   * from -- and the alternative was leaving a dispatch-visible write ungated while a new
   * permission was seeded.
   *
   * TO DO: rename to ManagePointHub. It is not a code-only rename -- the string is compared
   * case-sensitively against dbo.Permission, so it needs a seed migration that renames the row
   * (or adds the new one and re-grants), deployed by hand like every other RailCrew database
   * change, with the constant here changed in the same release. Doing it piecemeal locks
   * LocationAdmin out of hub management, so the two halves have to ship together.
   */
  AddPointHub: "AddPointHub",

  /**
   * Add a client point.
   *
   * The only write on the Points screens that creates a row rather than a link. Held apart from
   * AddPointHub because the blast radius is different: a bad hub link is a wrong option in a
   * dropdown, whereas a bad point is a second copy of a yard that trips, events and invoices
   * then accumulate against separately. Intended for LocationAdmin (and Admin).
   *
   * Seeded by Railcrew/Scripts/Migration/2026/AddPoint/20260826_AALLAN_01_AddCreatePointPermission.sql
   * (applied to QA/db13 2026-08-27, PermissionId 27, granted to LocationAdmin and Admin).
   * The gate is convenience -- OrderApi authorizes the create itself, validates the coordinate,
   * and runs the nearby-duplicate check regardless of what the browser sends. That is what
   * actually protects the table.
   */
  CreatePoint: "CreatePoint",
} as const;

export interface PointPermissions {
  canAddPointHub: boolean;
  canCreatePoint: boolean;
}

/** What the signed-in user may change on the Points screens. Read-only is the default. */
export const usePointPermissions = (): PointPermissions => {
  const { userHasPermission } = useContext(AuthContext);

  return {
    canAddPointHub: userHasPermission(PointPermission.AddPointHub),
    canCreatePoint: userHasPermission(PointPermission.CreatePoint),
  };
};
